import PropTypes from 'prop-types';
import { and, equals, inc, propOr } from 'ramda';
import { isRequiredBoolean, isRequiredInput, nameIsValid } from '@@viewValidations';
import {
  ControlledGranteeTypeSelect,
  ControlledInput,
  ControlledRadioButton,
  ControlledSelect,
  ControlledSocialSecurityOrganismCode,
  ControlledSSInput
} from '@@viewCommonComponents/controlled';
import GranteeBirthDate from '@@viewCommonComponents/GranteeBirthdate';
import { booleanOptions, civilityOptions } from '@@viewCommonComponents/forms/options';
import TwinRanksSelect from '@@viewCommonComponents/TwinRanksSelect';
import { DashedTitle, Tag } from '@fasstech/middle-design-system';
import { FormLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import SSInputByAttachedTo from '../components/SSInputByAttachedTo.jsx';
import ReferentModal from '../components/ReferentModal.jsx';
import useGrantee from '../../hooks/useGrantee.js';
import { dateEligibility } from '@@serverHelpers/index.mjs';
import { attachedToEnum, offerCodeEnum } from '@@viewEnums';
import { isRequiredSelect } from '@@viewHelpers/validations/index.js';
import { isDisplayedGranteeSitPro } from '../../helpers/isDisplayedGranteeSitPro.js';

const GranteeForm = ({
  index,
  professionalSituations,
  regimeObligatoire,
  middleFeatures,
  getMiddleText,
  getMiddleBoolean,
  getMiddleNumber,
  selectedTab,
  minBirthDateReducChild,
  maxBirthDateReducChild,
  minBirthDateHospiChild,
  maxBirthDateHospiChild,
  offerCode,
  form
}) => {
  const {
    attachedToOptions,
    getFieldsName,
    attachedToValue,
    displayReferentModal,
    setDisplayReferentModal,
    referentForm,
    onSubmitReferentForm,
    currentReferent,
    granteeIsChild,
    granteeIsSpouse,
    granteeType,
    granteeBirthdate,
    displayGeneralView,
    granteeTypeIsEmpty
  } = useGrantee({
    granteeIndex: index,
    middleFeatures,
    getMiddleText,
    getMiddleBoolean
  });

  const eligibilityReduc =
    !minBirthDateReducChild && !maxBirthDateReducChild
      ? null
      : dateEligibility(granteeBirthdate, minBirthDateReducChild, maxBirthDateReducChild);
  const eligibilityHospi =
    !minBirthDateHospiChild && !maxBirthDateHospiChild
      ? null
      : dateEligibility(granteeBirthdate, minBirthDateHospiChild, maxBirthDateHospiChild);
  const effectDate = form.watch('offerEffectDate');
  return (
    <FormLayout className={`${selectedTab !== inc(index) ? 'hidden' : ''}`}>
      <DashedTitle>{getMiddleText('BENEFICIAIRE_LABEL_GENERAL_VIEW_SUBTITLE')}</DashedTitle>

      <ControlledGranteeTypeSelect
        name={getFieldsName('type')}
        form={form}
        rules={isRequiredInput(getMiddleBoolean('BENEFICIAIRE_TYPE_EST_OBLIGATOIRE'))}
        label={getMiddleText('BENEFICIAIRE_TYPE_LABEL')}
        getMiddleText={getMiddleText}
        disabled={and(
          getMiddleBoolean('BENEFICIAIRE_TYPE_EST_OBLIGATOIRE'),
          getMiddleBoolean('BENEFICIAIRE_TYPE_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="BENEFICIAIRE_TYPE"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_TYPE_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_TYPE_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_TYPE_EST_DEMANDE_DONNEES_SOUSCRIPTION')) &&
          !granteeTypeIsEmpty
        }
      />

      {granteeIsChild && equals(offerCode, offerCodeEnum.SELF_EMPLOYED_WORKER_PROTECTION) && (
        <div className={'flex flex-col items-start w-full'}>
          {eligibilityReduc && (
            <div className="p-2 flex items-center">
              <Tag label={eligibilityReduc.label} variant={eligibilityReduc.variant} />
              <span className="ml-1">à la rente éducation</span>
            </div>
          )}
          {eligibilityHospi && (
            <div className="p-2 flex items-center">
              <Tag label={eligibilityHospi.label} variant={eligibilityHospi.variant} />
              <span className="ml-1">à l'hospitalisation des enfants</span>
            </div>
          )}
        </div>
      )}

      <ControlledRadioButton
        name={getFieldsName('civility')}
        form={form}
        options={civilityOptions}
        rules={isRequiredSelect(
          civilityOptions,
          getMiddleBoolean('BENEFICIAIRE_CIVILITE_EST_OBLIGATOIRE')
        )}
        disabled={and(
          getMiddleBoolean('BENEFICIAIRE_CIVILITE_EST_OBLIGATOIRE'),
          getMiddleBoolean('BENEFICIAIRE_CIVILITE_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="BENEFICIAIRE_CIVILITE"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_CIVILITE_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_CIVILITE_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_CIVILITE_EST_DEMANDE_DONNEES_SOUSCRIPTION'))
        }
      />

      <ControlledInput
        name={getFieldsName('lastName')}
        label={getMiddleText('BENEFICIAIRE_NOM_LABEL')}
        form={form}
        rules={nameIsValid(getMiddleBoolean('BENEFICIAIRE_NOM_EST_OBLIGATOIRE'))}
        disabled={and(
          getMiddleBoolean('BENEFICIAIRE_NOM_EST_OBLIGATOIRE'),
          getMiddleBoolean('BENEFICIAIRE_NOM_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="BENEFICIAIRE_NOM"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_NOM_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_NOM_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_NOM_EST_DEMANDE_DONNEES_SOUSCRIPTION'))
        }
      />

      <ControlledInput
        name={getFieldsName('birthName')}
        label={getMiddleText('BENEFICIAIRE_NOM_NAISSANCE_LABEL')}
        form={form}
        rules={nameIsValid(getMiddleBoolean('BENEFICIAIRE_NOM_NAISSANCE_EST_OBLIGATOIRE'))}
        data-testid="BENEFICIAIRE_NOM_NAISSANCE"
        isDisplayed={getMiddleBoolean('BENEFICIAIRE_NOM_NAISSANCE_EST_NECESSAIRE')}
      />

      <ControlledInput
        name={getFieldsName('firstName')}
        label={getMiddleText('BENEFICIAIRE_PRENOM_LABEL')}
        form={form}
        rules={nameIsValid(getMiddleBoolean('BENEFICIAIRE_PRENOM_EST_OBLIGATOIRE'))}
        disabled={and(
          getMiddleBoolean('BENEFICIAIRE_PRENOM_EST_OBLIGATOIRE'),
          getMiddleBoolean('BENEFICIAIRE_PRENOM_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="BENEFICIAIRE_PRENOM"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_PRENOM_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_PRENOM_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_PRENOM_EST_DEMANDE_DONNEES_SOUSCRIPTION'))
        }
      />

      <GranteeBirthDate
        granteeType={granteeType}
        granteeIsChild={granteeIsChild}
        getFieldsName={getFieldsName}
        form={form}
        disabled={getMiddleBoolean('BENEFICIAIRE_DATE_NAISSANCE_EST_DEMANDE_DONNEES_CONTRAT')}
        getMiddleText={getMiddleText}
        getMiddleBoolean={getMiddleBoolean}
        getMiddleNumber={getMiddleNumber}
        data-testid="BENEFICIAIRE_DATE_NAISSANCE"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_DATE_NAISSANCE_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_DATE_NAISSANCE_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_DATE_NAISSANCE_EST_DEMANDE_DONNEES_SOUSCRIPTION'))
        }
        effectDate= {effectDate}
      />

      <ControlledRadioButton
        label={getMiddleText('BENEFICIAIRE_ID_TNS_LABEL')}
        name={getFieldsName('isTNS')}
        options={booleanOptions}
        rules={isRequiredBoolean(getMiddleBoolean('BENEFICIAIRE_ID_TNS_EST_OBLIGATOIRE'))}
        form={form}
        data-testid="BENEFICIAIRE_IS_TNS"
        disabled={getMiddleBoolean('BENEFICIAIRE_ID_TNS_EST_DEMANDE_DONNEES_CONTRAT')}
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_ID_TNS_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_ID_TNS_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_ID_TNS_EST_DEMANDE_DONNEES_SOUSCRIPTION')) &&
          granteeIsSpouse
        }
      />

      <DashedTitle isDisplayed={displayGeneralView}>
        {getMiddleText('ADHERENT_GENERAL_VIEW_SUBTITLE2')}
      </DashedTitle>

      <ControlledSelect
        name={getFieldsName('CSPs.0.categoryLvl1CSPExternalId')}
        label={getMiddleText('BENEFICIAIRE_SIT_PRO_LABEL')}
        rules={isRequiredSelect(
          professionalSituations,
          getMiddleBoolean('BENEFICIAIRE_SIT_PRO_EST_OBLIGATOIRE')
        )}
        form={form}
        data-testid="BENEFICIAIRE_SIT_PRO"
        options={professionalSituations || []}
        disabled={
          getMiddleBoolean('BENEFICIAIRE_SIT_PRO_EST_DEMANDE_DONNEES_CONTRAT') &&
          getMiddleBoolean('BENEFICIAIRE_SIT_PRO_EST_OBLIGATOIRE')
        }
        isDisplayed={isDisplayedGranteeSitPro({
          getMiddleBoolean,
          granteeIsChild
        })}
      />

      <ControlledSelect
        name={getFieldsName('socialSecurities.0.regime')}
        label={getMiddleText('BENEFICIAIRE_REGIME_LABEL')}
        form={form}
        options={propOr([], 'regimes', regimeObligatoire)}
        rules={isRequiredSelect(
          propOr([], 'regimes', regimeObligatoire),
          getMiddleBoolean('BENEFICIAIRE_REGIME_EST_OBLIGATOIRE')
        )}
        disabled={and(
          getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_CONTRAT'),
          getMiddleBoolean('BENEFICIAIRE_REGIME_EST_OBLIGATOIRE')
        )}
        data-testid="BENEFICIAIRE_REGIME"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_REGIME_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION'))
        }
      />

      <ControlledSelect
        name={getFieldsName('attachedSocialSecurities.0.type')}
        label={getMiddleText('BENEFICIAIRE_NSS_RATT_A_LABEL')}
        form={form}
        options={attachedToOptions}
        rules={isRequiredSelect(
          attachedToOptions,
          getMiddleBoolean('BENEFICIAIRE_NSS_EST_OBLIGATOIRE')
        )}
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_NSS_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_NSS_RATT_A_EST_NECESSAIRE')
        }
      />

      <SSInputByAttachedTo
        attachedToValue={attachedToValue}
        getFieldsName={getFieldsName}
        form={form}
        getMiddleText={getMiddleText}
        setDisplayReferentModal={setDisplayReferentModal}
        currentReferent={currentReferent}
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_NSS_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_NSS_RATT_A_EST_NECESSAIRE')
        }
      />

      {equals(attachedToEnum.EN_NOM_PROPRE, attachedToValue) &&
        getMiddleBoolean('BENEFICIAIRE_ORG_RATT_EST_NECESSAIRE') &&
        getMiddleBoolean('BENEFICIAIRE_REGIME_EST_NECESSAIRE') &&
        (getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION')) && (
        <ControlledSocialSecurityOrganismCode
          name={getFieldsName('socialSecurities.0.organismCode')}
          regimeName={getFieldsName('socialSecurities.0.regime')}
          rules={isRequiredInput(getMiddleBoolean('BENEFICIAIRE_ORG_RATT_EST_OBLIGATOIRE'))}
          regimeObligatoire={regimeObligatoire}
          getMiddleText={getMiddleText}
          tooltipOrganismCode={getMiddleText('BENEFICIAIRE_ORG_RATT_INFO_BULLE')}
          labelOrganismCode={getMiddleText('BENEFICIAIRE_ORG_RATT_LABEL')}
          tooltipRegime={getMiddleText('BENEFICIAIRE_ORG_RATT_REGIME_INFO_BULLE')}
          tooltipCaisse={getMiddleText('BENEFICIAIRE_ORG_RATT_CAISSE_INFO_BULLE')}
          tooltipCentre={getMiddleText('BENEFICIAIRE_ORG_RATT_CENTRE_INFO_BULLE')}
          data-testid="BENEFICIAIRE_ORG_RATT"
          className={'w-[112%]!'}
        />
      )}

      <TwinRanksSelect
        form={form}
        rules={isRequiredInput(getMiddleBoolean('BENEFICIAIRE_RANG_EST_OBLIGATOIRE'))}
        name={getFieldsName('socialSecurities.0.twinsRank')}
        label={getMiddleText('BENEFICIAIRE_RANG_LABEL')}
        tooltip={getMiddleText('BENEFICIAIRE_RANG_INFO_BULLE')}
        disabled={and(
          getMiddleBoolean('BENEFICIAIRE_RANG_EST_DEMANDE_DONNEES_CONTRAT'),
          getMiddleBoolean('BENEFICIAIRE_RANG_EST_OBLIGATOIRE')
        )}
        data-testid="BENEFICIAIRE_RANG"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_RANG_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_RANG_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_RANG_EST_DEMANDE_DONNEES_SOUSCRIPTION'))
        }
      />

      <ControlledSSInput
        SSInputName={getFieldsName('socialSecurities.0.number')}
        birthDateInputName={getFieldsName('birthDate')}
        label={getMiddleText('BENEFICIAIRE_NSS_LABEL')}
        form={form}
        mandatory={getMiddleBoolean('BENEFICIAIRE_NSS_EST_OBLIGATOIRE')}
        disabled={false}
        data-testid="BENEFICIAIRE_NSS"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_NSS_EST_NECESSAIRE') &&
          !getMiddleBoolean('BENEFICIAIRE_NSS_RATT_A_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_NSS_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_NSS_EST_DEMANDE_DONNEES_SOUSCRIPTION'))
        }
      />

      <ControlledRadioButton
        label={getMiddleText('ADHERENT_TELETRANSMISSION_QUESTION', {
          clientName: getMiddleText('GENERAL_CLIENT_NOM')
        })}
        name={getFieldsName('socialSecurities.0.isTeletransmission')}
        id={getFieldsName('socialSecurities.0.isTeletransmission')}
        form={form}
        rules={{
          ...isRequiredBoolean(getMiddleBoolean('BENEFICIAIRE_TELETRANSMISSION_EST_OBLIGATOIRE'))
        }}
        options={booleanOptions}
        booleanValue={getMiddleBoolean('ADHERENT_TELETRANSMISSION_REPONSE')}
        data-testid="ADHERENT_TELETRANSMISSION_REPONSE"
        message={getMiddleText('ADHERENT_TELETRANSMISSION_MESSAGE_INFO')}
        isDisplayed={getMiddleBoolean('BENEFICIAIRE_TELETRANSMISSION_EST_NECESSAIRE')}
      />

      <ReferentModal
        getFieldsName={getFieldsName}
        setDisplayReferentModal={setDisplayReferentModal}
        displayReferentModal={displayReferentModal}
        getMiddleText={getMiddleText}
        onSubmitReferentForm={onSubmitReferentForm}
        form={referentForm}
      />
    </FormLayout>
  );
};

GranteeForm.propTypes = {
  regimeObligatoire: PropTypes.object,
  index: PropTypes.number,
  handleRemoveGrantee: PropTypes.func,
  getValues: PropTypes.func,
  displayDeletionModal: PropTypes.bool,
  setDisplayDeletionModal: PropTypes.func,
  isDisabledAddingChild: PropTypes.func
};

export default GranteeForm;
